<template>
  <div class="index-page">

    <h-breadcrumb :paths="paths"/>
    <!-- 道具資訊 -->
    <h-skeleton v-if="loading" class="h-mt-30 h-mb-20"/>
    <h-product
      v-else-if="spendingId"
      :image="productDetailed.imageUrl"
      :name="productDetailed.productName"
      :spending-id="spendingId"
      :coin-pay-qty="coinPayQty + $t('coinPayment.amount')"
    />
    <div class="info">
      <div v-if="coinNeedQty > 0" class="not-enough">
        {{ `${$t('notEnoughBalance')}！` }}
      </div>
      <h-user-balance class="w-full" :coin-need-qty="coinNeedQty"/>
      <!-- 到商城充值 -->
      <template v-if="!loading">
        <div
          v-if="coinNeedQty > 0 || !spendingId"
          class="flex-col justify-center align-center w-full"
        >
          <h-button
            block
            size="large"
            shape="round"
            @click="handleGoCoinStore"
          >
            <font-icon icon="fas fa-credit-card" />
            {{ $t('coinPayment.buyMoreHcoin') }}
          </h-button>
          <div v-if="pointCardRedemptionEnabled" class="my-30">
            {{ $t('or') }}
          </div>
          <h-button
            v-if="pointCardRedemptionEnabled"
            block
            size="large"
            shape="round"
            to="/redeem"
          >
            <font-icon icon="fas fa-credit-card" />
            {{ $t('coinPayment.redeemCode') }}
          </h-button>
          <div class="my-30">
            {{ $t('or') }}
          </div>
          <!-- 訂閱服務橫幅圖片 -->
          <div class="subscription-banner">
            <img
              src="@/assets/image/subscription/monthly_card_banner.jpg"
              alt="Monthly Card Banner"
              class="banner-image"
            />
          </div>
          <h-button
            block
            size="large"
            shape="round"
            @click="handleOpenSubscriptionOverlay"
          >
            <font-icon icon="fas fa-calendar-check" />
            {{ $t('subscription.products.title') }}
          </h-button>
        </div>
        <h-button
          v-else
          block
          size="large"
          shape="round"
          :disabled="fetchingOrderComplete"
          @click="confirmBuy"
        >
          {{ $t('coinPayment.confirmBuy') + coinPayQty + $t('coinPayment.amount') }}
        </h-button>
      </template>
    </div>
    <h-loader v-if="fetchingOrderComplete" size="lg" overlay/>
  </div>
</template>
<script>
import HButton from '@/components/base/HButton.vue'
import HUserBalance from '@/components/HUserBalance.vue'
import HSkeleton from '@/components/base/HSkeleton.vue'
import HLoader from '@/components/base/HLoader.vue'
import HBreadcrumb from '@/components/base/HBreadcrumb.vue'
import HProduct from '@/components/payment/HProduct.vue'
import { toErrorPage } from '@/utils'
import { h365PlatformApi } from '@/plugin/axios'
import iframeMessaging, { MESSAGE_TYPES } from '@/utils/iframeMessaging'
import { getSubscriptionProducts } from '@/api/subscription'
import { POINT_CARD_REDEMPTION_ENABLED } from '@/config/features'

export default {
  name: 'indexPage',
  components: {
    HButton,
    HUserBalance,
    HSkeleton,
    HLoader,
    HBreadcrumb,
    HProduct
  },
  data() {
    return {
      spendingId: '',
      coinPayQty: '',
      coinNeedQty: 0,
      loading: false,
      fetchingOrderComplete: false,
      showSnackBar: false,
      message: '',
      productDetailed: {
        productName: '',
        imageUrl: ''
      },
      trackPayload: {}
    }
  },
  computed: {
    pointCardRedemptionEnabled: () => POINT_CARD_REDEMPTION_ENABLED,
    paths() {
      const applicationUrl = this.$cookies.get('h365_applicationUrl') || ''
      const urlHome = this.$cookies.get('h365_urlHome') || ''
      const paths = [
        ...(applicationUrl && [{
          text: this.$t('game'),
          href: applicationUrl
        }]),
        ...(urlHome && [{
          icon: 'fa-solid fa-house',
          href: urlHome
        }]),
        {
          text: this.$t('payment.purchase'),
        }
      ]
      return paths
    }
  },
  created() {
    this.spendingId = this.$route.query.paymentId
    if (this.spendingId) this.handleCoinDetail()
    this.setupIframeMessaging()
    this.checkPendingMessages()

    // 通知父頁面支付頁面已準備就緒
    this.$nextTick(() => {
      iframeMessaging.notifyPaymentReady('index', {
        canHandleProductSelection: true,
        canOpenSubscriptionOverlay: true,
        hasSpendingId: !!this.spendingId
      })
    })
  },
  beforeDestroy() {
    this.cleanupIframeMessaging()
  },
  methods: {
    handleGoCoinStore() {
      // matomo trackGoal 選充值方式
      window?._paq?.push(['trackGoal', 12])

      this.$router.push({ name: 'store', query: this.$route.query })
    },
    handleCoinDetail() {
      if (this.$store.state.coinDetail) {
        const coinDetail = this.$store.state.coinDetail
        this.setCoinDetail(coinDetail)
      }
      else
        this.getCoinDetailed()
    },
    async getCoinDetailed(retryCount = 0) {
      this.loading = true
      const t1 = Date.now()
      const maxRetries = 3
      const retryDelay = 3000 // 3秒

      try {
        const url = '/coin/spending/' + this.spendingId
        const { data: response } = await h365PlatformApi.get(url)
        this.loading = false

        // 檢查是否需要重試
        if (response.code === -99 && retryCount < maxRetries) {
          console.log(`API 返回 -99，第 ${retryCount + 1} 次重試，等待 ${retryDelay}ms...`)
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          return this.getCoinDetailed(retryCount + 1)
        }

        if (response.code === 200) {
          const res = response.data
          switch (res.status) {
          case 0:
            this.$store.commit('setCoinDetail', res)
            this.setCoinDetail(res)
            break
          case 2: {
            // 订单已成功
            const error = new Error(this.$t('orderCompleted'))
            error.code = 'Get coin detail failed'
            throw error
          }
          default: {
            const error = new Error(this.$t('orderStatusNotFound' + `(${res.status})`))
            error.code = 'Get coin detail failed'
            throw error
          }
          }
        }
        else {
          const error = new Error(response.message ?? this.$t('orderResponseErr'))
          error.code = 'Get coin detail failed'
          throw error
        }
      }
      catch(error) {
        // matomo trackEvent
        window?._paq?.push(['trackEvent', '消費單', '獲取明細異常', JSON.stringify({
          spendingId: this.spendingId,
          timeSpent: Date.now() - t1,
          retryCount,
          error: error?.message ?? 'unkown error'
        })])
        toErrorPage(error?.code, error?.message)
      }
    },
    setCoinDetail(res) {
      this.productDetailed.productName = res.item.itemName
      this.productDetailed.imageUrl = res.item.imageUrl
      this.coinPayQty = res.coinPayQty
      this.coinNeedQty = res.coinNeedQty
      this.trackPayload = {
        id: this.spendingId,
        serviceId: res.serviceId,
        productId: res.item.itemId,
        productName: res.item.itemName,
        orderUsdAmount: res.orderUsdAmount
      }
    },
    handleError(errorMsg) {
      const message = this.$t(errorMsg)
      toErrorPage('Get coin detail failed', message)
    },
    async confirmBuy() {
      this.fetchingOrderComplete = true
      // matomo trackEvent
      window?._paq?.push(['trackEvent', '消費單', '確認支付', this.spendingId])

      const t1 = Date.now()
      try {
        const url = '/coin/orderComplete/' + this.spendingId
        const { data: response } = await h365PlatformApi.get(url)

        if (response.code !== 200) throw new Error(response?.message ?? 'no msg')
        
        // matomo trackEvent
        window?._paq?.push(['trackEvent', '消費單', '確認支付完成', JSON.stringify(this.trackPayload), Date.now() - t1])
        // matomo trackGoal 消費成功 TBC (Goal deleted, must create new one)
        // window?._paq?.push(['trackGoal', 3, Number(this.trackPayload.orderUsdAmount)])

        const applicationUrl = response.data.applicationUrl
        this.$router.push({
          name: 'coinSpending-detail',
          params: {
            id: this.spendingId
          },
          query: {
            ...this.$route.query,
            applicationUrl
          }
        })
      }
      catch (e) {
        this.$message.error(this.$t('coinSpendingErr'))
        // matomo trackEvent
        window?._paq?.push(['trackEvent', '消費單', '確認支付異常', JSON.stringify({
          spendingId: this.spendingId,
          timeSpent: Date.now() - t1,
          error: e?.message ?? 'unkown error'
        })])
      }
      finally {
        this.fetchingOrderComplete = false
      }
    },
    handleImageError() {
      this.productDetailed.imageUrl = require('@/assets/image/item-default.png')
    },
    async handleOpenSubscriptionOverlay() {
      // 如果在 iframe 環境中，直接觸發開啟父頁面 overlay
      if (iframeMessaging.getIsIframe()) {
        try {
          // 獲取訂閱產品資料
          const { data } = await getSubscriptionProducts()

          if (data.code === 200) {
            const products = data.data || []
            const enabledProducts = products.filter(product => product.status)

            // 請求父頁面開啟 overlay 並傳遞產品資料
            iframeMessaging.requestOpenSubscriptionOverlay(enabledProducts, this.$store.state)

            // matomo trackEvent
            window?._paq?.push(['trackEvent', '充值頁面', '開啟訂閱服務 Overlay', enabledProducts.length])
          }
          else {
            console.error('獲取訂閱產品失敗:', data.message)
            // 降級到傳統跳轉
            this.$router.push('/subscription/products')
          }
        }
        catch (error) {
          console.error('獲取訂閱產品異常:', error)
          // 降級到傳統跳轉
          this.$router.push('/subscription/products')
        }
      }
      else {
        // 非 iframe 環境，跳轉到訂閱產品頁面
        this.$router.push('/subscription/products')
      }
    },

    /**
     * 設置 iframe 通訊
     */
    setupIframeMessaging() {
      const frameInfo = this._getIframeContext()
      console.group(`[🏠 INDEX-IFRAME] [${frameInfo}] 🔧 設置 iframe 通訊`)

      const isIframe = iframeMessaging.getIsIframe()
      console.log('[🏠 INDEX-IFRAME] 🌐 環境檢測:', {
        isIframe,
        currentUrl: window.location.href,
        parentExists: window.parent !== window
      })

      if (!isIframe) {
        console.log('[🏠 INDEX-IFRAME] ℹ️ 非 iframe 環境，跳過通訊設置')
        console.groupEnd()
        return
      }

      console.log('[🏠 INDEX-IFRAME] 📡 註冊訊息處理器:', {
        messageTypes: [
          MESSAGE_TYPES.SUBSCRIPTION_PRODUCT_SELECTED,
          MESSAGE_TYPES.OPEN_SUBSCRIPTION_OVERLAY
        ],
        handlerFunctions: [
          'handleProductSelectionFromParent',
          'handleOpenSubscriptionOverlayFromParent'
        ],
        timestamp: new Date().toISOString()
      })

      // 監聽父頁面產品選擇消息，導航到 products 頁面
      iframeMessaging.onMessage(MESSAGE_TYPES.SUBSCRIPTION_PRODUCT_SELECTED, (data) => {
        console.log('[🏠 INDEX-IFRAME] 📨 收到父頁面產品選擇訊息，準備導航:', {
          messageType: MESSAGE_TYPES.SUBSCRIPTION_PRODUCT_SELECTED,
          timestamp: new Date().toISOString(),
          dataReceived: !!data,
          targetRoute: 'subscription-products'
        })
        this.handleProductSelectionFromParent(data)
      })

      // 監聽父頁面開啟訂閱 overlay 消息
      iframeMessaging.onMessage(MESSAGE_TYPES.OPEN_SUBSCRIPTION_OVERLAY, (data) => {
        console.log('[🏠 INDEX-IFRAME] 📨 收到父頁面開啟 Overlay 訊息，準備導航:', {
          messageType: MESSAGE_TYPES.OPEN_SUBSCRIPTION_OVERLAY,
          timestamp: new Date().toISOString(),
          dataReceived: !!data,
          targetRoute: 'subscription-products'
        })
        this.handleOpenSubscriptionOverlayFromParent(data)
      })

      console.log('[🏠 INDEX-IFRAME] ✅ iframe 通訊設置完成')
      console.groupEnd()
    },

    /**
     * 清理 iframe 通訊
     */
    cleanupIframeMessaging() {
      if (!iframeMessaging.getIsIframe()) return
      console.log('[🏠 INDEX-IFRAME] 🧹 清理 iframe 通訊')
      iframeMessaging.offMessage(MESSAGE_TYPES.SUBSCRIPTION_PRODUCT_SELECTED)
      iframeMessaging.offMessage(MESSAGE_TYPES.OPEN_SUBSCRIPTION_OVERLAY)
    },

    /**
     * 處理來自父頁面的產品選擇 - 導航到 products 頁面
     */
    async handleProductSelectionFromParent(data) {
      const startTime = performance.now()
      console.group('[🏠 INDEX-IFRAME] 🚀 處理產品選擇，導航到產品頁面')

      console.log('[🏠 INDEX-IFRAME] 📥 接收到的數據:', {
        data,
        timestamp: new Date().toISOString(),
        currentRoute: this.$route.name
      })

      try {
        // 確保頁面準備就緒
        await this.waitForDataToLoad()

        // 導航到 subscription products 頁面，並保留原始數據
        console.log('[🏠 INDEX-IFRAME] 🧭 準備導航到產品頁面:', {
          targetRoute: 'subscription-products',
          preservedData: data,
          currentQuery: this.$route.query
        })

        // 將數據暫存到 sessionStorage，讓 products 頁面可以取得
        if (data) {
          sessionStorage.setItem('pendingProductSelection', JSON.stringify({
            ...data,
            timestamp: Date.now(),
            fromIndex: true
          }))
          console.log('[🏠 INDEX-IFRAME] 💾 數據已暫存到 sessionStorage')
        }

        await this.$router.push({
          name: 'subscription-products',
          query: {
            ...this.$route.query,
            autoSelect: 'true' // 標記自動選擇模式
          }
        })

        console.log('[🏠 INDEX-IFRAME] ✅ 導航完成:', {
          totalTime: `${(performance.now() - startTime).toFixed(2)}ms`,
          targetRoute: 'subscription-products'
        })

      }
      catch (error) {
        console.error('[🏠 INDEX-IFRAME] ❌ 導航錯誤:', {
          error: error.message,
          stack: error.stack,
          totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
        })

        // 發送錯誤狀態給父頁面
        iframeMessaging.sendPaymentStatus('FAIL', null, {
          error: 'NAVIGATION_ERROR',
          details: error.message
        })
      }

      console.groupEnd()
    },

    /**
     * 處理來自父頁面的開啟訂閱 Overlay 請求 - 導航到 products 頁面
     */
    async handleOpenSubscriptionOverlayFromParent(data) {
      const startTime = performance.now()
      console.group('[🏠 INDEX-IFRAME] 🚀 處理開啟 Overlay 請求，導航到產品頁面')

      console.log('[🏠 INDEX-IFRAME] 📥 接收到的數據:', {
        data,
        timestamp: new Date().toISOString(),
        currentRoute: this.$route.name
      })

      try {
        // 確保頁面準備就緒
        await this.waitForDataToLoad()

        // 導航到 subscription products 頁面，並標記為 overlay 模式
        console.log('[🏠 INDEX-IFRAME] 🧭 準備導航到產品頁面（Overlay 模式）:', {
          targetRoute: 'subscription-products',
          preservedData: data,
          currentQuery: this.$route.query
        })

        // 將數據暫存到 sessionStorage，讓 products 頁面可以取得
        if (data) {
          sessionStorage.setItem('pendingOverlayRequest', JSON.stringify({
            ...data,
            timestamp: Date.now(),
            fromIndex: true,
            mode: 'overlay'
          }))
          console.log('[🏠 INDEX-IFRAME] 💾 Overlay 數據已暫存到 sessionStorage')
        }

        await this.$router.push({
          name: 'subscription-products',
          query: {
            ...this.$route.query,
            mode: 'overlay' // 標記為 overlay 模式
          }
        })

        console.log('[🏠 INDEX-IFRAME] ✅ 導航完成（Overlay 模式）:', {
          totalTime: `${(performance.now() - startTime).toFixed(2)}ms`,
          targetRoute: 'subscription-products'
        })

      }
      catch (error) {
        console.error('[🏠 INDEX-IFRAME] ❌ 導航錯誤（Overlay 模式）:', {
          error: error.message,
          stack: error.stack,
          totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
        })

        // 發送錯誤狀態給父頁面
        iframeMessaging.sendPaymentStatus('FAIL', null, {
          error: 'OVERLAY_NAVIGATION_ERROR',
          details: error.message
        })
      }

      console.groupEnd()
    },

    /**
     * 等待數據載入完成
     */
    async waitForDataToLoad(maxWaitTime = 5000) {
      return new Promise((resolve, reject) => {
        const startTime = Date.now()

        const checkReady = () => {
          // 檢查頁面是否已準備就緒
          if (this.$el && !this.loading) {
            console.log('[🏠 INDEX-IFRAME] ✅ 頁面資料準備完成:', {
              waitTime: `${Date.now() - startTime}ms`
            })
            resolve()
            return
          }

          if (Date.now() - startTime > maxWaitTime) {
            console.warn('[🏠 INDEX-IFRAME] ⚠️ 等待頁面準備逾時')
            reject(new Error('頁面準備逾時'))
            return
          }

          // 每100ms檢查一次
          setTimeout(checkReady, 100)
        }

        checkReady()
      })
    },

    /**
     * 處理直接在 index 頁面的 overlay 請求（如果需要的話）
     */
    async handleDirectOverlayRequest(data) {
      const startTime = performance.now()
      console.group('[🏠 INDEX-IFRAME] 🚀 直接處理 Overlay 請求')

      try {
        // 等待頁面準備就緒
        await this.waitForDataToLoad()

        // 如果有現成的 handleOpenSubscriptionOverlay 方法，可以在這裡調用
        // 或者直接發送現有數據到父頁面
        console.log('[🏠 INDEX-IFRAME] 📦 準備發送現有數據到父頁面')

        // 這裡可以根據需要實現直接的 overlay 處理邏輯
        // 目前先導航到 products 頁面讓它處理
        await this.handleOpenSubscriptionOverlayFromParent(data)

        console.log('[🏠 INDEX-IFRAME] ✅ 直接 Overlay 處理完成:', {
          totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
        })

      }
      catch (error) {
        console.error('[🏠 INDEX-IFRAME] ❌ 直接 Overlay 處理錯誤:', {
          error: error.message,
          totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
        })

        // 發送錯誤狀態給父頁面
        iframeMessaging.sendPaymentStatus('FAIL', null, {
          error: 'DIRECT_OVERLAY_ERROR',
          details: error.message
        })
      }

      console.groupEnd()
    },

    /**
     * 獲取 iframe 上下文信息
     */
    _getIframeContext() {
      const path = window.location.pathname.split('/').pop() || 'root'
      const sessionId = Math.random().toString(36).substr(2, 4)
      return `${path}-${sessionId}`
    },

    /**
     * 檢查是否有暫存的消息或 sessionStorage 中的數據
     */
    checkPendingMessages() {
      console.group('[🏠 INDEX-IFRAME] 🔍 檢查暫存的消息數據')

      // 檢查 sessionStorage 中的產品選擇數據
      this.checkPendingData('pendingProductSelection', (data) => {
        console.log('[🏠 INDEX-IFRAME] ⏳ 延遲處理暫存的產品選擇')
        this.handleProductSelectionFromParent(data)
      })

      // 檢查 sessionStorage 中的 Overlay 請求數據
      this.checkPendingData('pendingOverlayRequest', (data) => {
        console.log('[🏠 INDEX-IFRAME] ⏳ 延遲處理暫存的 Overlay 請求')
        this.handleOpenSubscriptionOverlayFromParent(data)
      })

      console.groupEnd()
    },

    /**
     * 檢查指定的暫存數據
     */
    checkPendingData(storageKey, handler) {
      try {
        const pendingData = sessionStorage.getItem(storageKey)

        if (!pendingData) {
          console.log(`[🏠 INDEX-IFRAME] ℹ️ 無暫存數據 (${storageKey})`)
          return
        }

        const parsedData = JSON.parse(pendingData)
        console.log(`[🏠 INDEX-IFRAME] 📦 發現暫存數據 (${storageKey}):`, {
          data: parsedData,
          timestamp: new Date(parsedData.timestamp).toISOString(),
          fromIndex: parsedData.fromIndex,
          mode: parsedData.mode || 'default',
          age: `${Date.now() - parsedData.timestamp}ms`
        })

        // 清除暫存數據（避免重複處理）
        sessionStorage.removeItem(storageKey)
        console.log(`[🏠 INDEX-IFRAME] 🗑️ 已清除暫存數據 (${storageKey})`)

        // 檢查數據是否過期（30秒內有效）
        if (Date.now() - parsedData.timestamp > 30000) {
          console.warn(`[🏠 INDEX-IFRAME] ⚠️ 暫存數據已過期，忽略處理 (${storageKey})`)
          return
        }

        // 等待 DOM 準備就緒後再處理
        this.$nextTick(() => {
          setTimeout(() => {
            handler(parsedData)
          }, 500) // 給頁面初始化一些時間
        })

      }
      catch (error) {
        console.error(`[🏠 INDEX-IFRAME] ❌ 處理暫存數據錯誤 (${storageKey}):`, error)
        sessionStorage.removeItem(storageKey) // 清除損壞的數據
      }
    }
  }
}
</script>
<style lang="scss" scoped>

.flex-col {
  display: flex;
  flex-direction: column;
}
.justify-center {
  justify-content: center;
}
.align-center {
  align-items: center;
}
.w-full {
  width: 100%;
}
.my-30 {
  margin: 30px 0;
}
.index-page {
  flex: 1;

  .info {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }
}
.item-info {
  background-color: #ffffff;
  padding: 8px;
  border-radius: 8px;
  margin-bottom: 12px;
  display: flex;
  &-image {
    width: 80px;
    img {
       width: 100%;
    }
  }
  &-content {
    width: calc(100% - 80px);
    padding: 0px 12px;
    line-height: 20px;
    * {
      margin-bottom: 4px;
    }
    :last-child {
      margin-bottom: 0px;
    }
  }
  &-title {
    color: #262626;
    font-weight: bold;
  }
  &-coin {
    color: #595959;
    background: #FFFBE6;
    height: 20px;
    padding: 0px 8px;
    display: inline-flex;
  }
  &-serial-number {
    color: #595959;
  }
}
.info {  
  background-color: #ffffff;
  border-radius: 16px;
  padding: 32px;
}
.not-enough {
  color: color(black-2);
  font-size: 20px;
  line-height: 28px;
  margin-bottom: 32px;
  text-align: center;
  width: 100%;
}
.subscription-banner {
  width: 100%;
  margin-bottom: 16px;
  border-radius: 16px;
  overflow: hidden;

  .banner-image {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 16px;
  }
}
</style>
